import apiFieldMap from '../../components/Page/SignUp/Common/kycApiNodeMappings.json';
import { UseFormSetValue } from 'react-hook-form';
import { getCustomerUrl } from './utils';
import HttpClient from './httpClient';
import { updateComponentData } from '../../components/Utils/CommonBaseClass';
import { Logger } from './logger';
const httpClient = HttpClient.getClient();

/**
 * This file houses the methods to call the autoSave API for the KYC pages
 * and save the data on the onBlur of each KYC field
 * The API is called with the field name and the value of the field is being fetched from the kycApiNodeMappings.json file
 */


type FieldMap = Record<string, string>;
type ApiPageConfig = {
  apiName: string;
  fields: FieldMap;
};
type ApiFieldMap = Record<string, ApiPageConfig>;

/**
 * Calling the GET autosave api to get the previously saved data
 * @returns 
 */
export const getAutosavedDataFromOnboardingFormDataApi = async () => {
    try {
        let apiName: any = '/onboardingFormData'
        return await httpClient.get(getCustomerUrl(apiName,false));
    } catch (err) {
        Logger.error("BusinessUserSignUp.tsx", "getPreviouslySavedDataFromOnboardingApi", "error", err);
        return err;
    }
}

/**
 * Method to delete the particular BO from the autoSave API as well
 * @param req 
 * @returns 
 */
export const updateOnboardingFormData = async (req: any) => {
    try {
        let apiName: any = '/onboardingFormData'
        return await httpClient.post(getCustomerUrl(apiName,false), req);
    } catch (err) {
        Logger.error("BusinessUserSignUp.tsx", "deleteFromAutoSaveAPI", "error", err);
        return err;
    }
}

/**
 * This method maps the node name and the page name from the KYC form to the KycApiNodeMappings.json file
 * and return the correct apiName and path for the field supported by the API
 * @param pageName 
 * @param fieldName 
 * @returns 
 */
function findFieldMapping(pageName: string, fieldName: string) {
    const typedApiFieldMap = apiFieldMap as ApiFieldMap;
    const pageConfig = typedApiFieldMap[pageName];
    if (!pageConfig) {
        Logger.warn(`Page "${pageName}" not found in API map.`);
        return null;
    }
    const path = pageConfig.fields[fieldName];
    if (!path) {
        Logger.warn(`Field "${fieldName}" not found on page "${pageName}".`);
        return null;
    }
    return {
        apiName: pageConfig.apiName,
        path,
    };
  }
  /**
   * This method replaces the index in the path string with the provided index of the field set
   * @param path 
   * @param index 
   * @returns 
   */
  export function updatePathIndex(path: string, index: number): string {
    try {
        const parts = path.split('[');
        if (parts && parts.length > 1 && (parts[0].endsWith('beneficialOwner') || parts[0].endsWith('owners'))) {
        const base = parts[0];
        const rest = parts.slice(1).join('[');
        const currentIndexMatch = rest.match(/^(\d+)\]/);
        if (currentIndexMatch) {
            const remaining = rest.substring(currentIndexMatch[0].length);
            return `${base}[${index}]${remaining}`;
        }
        }
        return path; // Return original path if pattern doesn't match
    }catch(err){
       Logger.error("autoSaveField.ts", "updatePathIndex", "error", err);
       return path; //returning the original path 
    }
  }
  
  /**
   * This method calls the autosave API to save the node data at the onBlur of each KYC field
   * @param pageName 
   * @param nodeName 
   * @param event 
   * @param setValue 
   * @param getValues 
   * @param dispatch 
   * @param businessState 
   * @param index 
   * @returns 
   */
export const saveNodeData = async (
  pageName: string,
  nodeName: string,
  event: any,
  setValue: UseFormSetValue<any>,
  getValues: any,
  dispatch: any,
  businessState?: any,
  index?: number
) => {
  // 1. Extract the value from event or getValues depending on special fields
  const value = ['stateOfIncorporation', 'locationDetailState', 'state', 'usCitizen'].includes(nodeName)
    ? event?.value ?? event?.target?.value
    : getValues(nodeName);

  // 2. Update form state immediately for responsive UI
  setValue(nodeName, value);

  // 3. Find API mapping (apiName + json path) for the field
  const match = findFieldMapping(pageName, nodeName);
  if (!match) {
    Logger.warn(`No mapping found for field: ${nodeName} on page: ${pageName}`);
    return;
  }
  const { apiName, path } = match;

  // 4. Prepare request payload object to send to server
  // If `index` is provided, update the path to point to that index in arrays
  let targetPath = path;
  if (index !== undefined) {
    targetPath = updatePathIndex(path, index);
  }

  // Initialize the request payload with the field value
  let req: any = { [targetPath]: value };

  // 5. Handle special cases based on page and nodeName
  if (index !== undefined) {
    // Cases where the field is inside an indexed array

    if (pageName === 'ownership-info-page') {
      // Special logic for ITIN/SSN toggling in ownership-info-page
      const configMap: Record<string, () => void> = {
        itin: () => {
          const taxTypePath = updatePathIndex('business.beneficialOwner[0].taxIdentification.type', index);
          const ssnPath = updatePathIndex('business.beneficialOwner[0].ssn', index);
          req[taxTypePath] = 'ITIN';
          req[ssnPath] = '';
        },
        ssn: () => {
          const taxTypePath = updatePathIndex('business.beneficialOwner[0].taxIdentification.type', index);
          const taxIdPath = updatePathIndex('business.beneficialOwner[0].taxIdentification.id', index);
          req[taxTypePath] = '';
          req[taxIdPath] = '';
        }
      };
      configMap[nodeName]?.();
    }

    if (pageName === 'jt-basic-info-page' || pageName === 'coowner-info-page') {
      // Similar toggling for JT and coowner pages
      const configMap: Record<string, () => void> = {
        itin: () => {
          const taxTypePath = updatePathIndex('owners[0].taxIdentification.type', index);
          const ssnPath = updatePathIndex('owners[0].ssn', index);
          req[taxTypePath] = 'ITIN';
          req[ssnPath] = '';
        },
        ssn: () => {
          const taxTypePath = updatePathIndex('owners[0].taxIdentification.type', index);
          const taxIdPath = updatePathIndex('owners[0].taxIdentification.id', index);
          req[taxTypePath] = '';
          req[taxIdPath] = '';
        }
      };
      configMap[nodeName]?.();
    }

    // Boolean conversion for authSignatory field (yes/no -> true/false)
    if (nodeName === 'authSignatory') {
      req[targetPath] = value === 'yes';
    }
  } else {
    // --- Cases where field is NOT in an indexed array

    req[path] = value;

    if (pageName === 'individual-customer-info-page') {
      // Special handling for individual customer page for SSN/ITIN/NONE
      const fieldConfigs: Record<string, { keys: string[], additional?: Record<string, any> }> = {
        itin: {
          keys: [
            "individual.ssn",
            "individual.secondaryIdentification.type",
            "individual.secondaryIdentification.id",
            "individual.secondaryIdentification.stateOfIssuance",
            "individual.secondaryIdentification.countryOfIssuance",
            "individual.secondaryIdentification.expiryDate",
          ],
          additional: {
            "individual.taxIdentification.type": "ITIN"
          }
        },
        ssn: {
          keys: [
            "individual.taxIdentification.id",
            "individual.taxIdentification.type",
            "individual.secondaryIdentification.type",
            "individual.secondaryIdentification.id",
            "individual.secondaryIdentification.stateOfIssuance",
            "individual.secondaryIdentification.countryOfIssuance",
            "individual.secondaryIdentification.expiryDate",
          ]
        },
        secondaryIdentificationId: {
          keys: [
            "individual.taxIdentification.id",
            "individual.taxIdentification.type",
            "individual.ssn",
          ]
        }
      };

      if (fieldConfigs[nodeName]) {
        const { keys, additional } = fieldConfigs[nodeName];
        const restReq = createRequestFromKeys(...keys);

        req = {
          ...req,
          ...restReq,
          ...(additional || {})
        };
      }
    }

    if (pageName === 'jt-basic-info-page') {
      // JT basic info SSN/ITIN toggling outside indexed array (assumed index 0)
      const configMap: Record<string, () => void> = {
        itin: () => {
          req['owners[0].taxIdentification.type'] = 'ITIN';
          req['owners[0].ssn'] = '';
        },
        ssn: () => {
          req['owners[0].taxIdentification.type'] = '';
          req['owners[0].taxIdentification.id'] = '';
        }
      };
      configMap[nodeName]?.();
    }
  }

  // 6. Call the API with prepared request payload and handle response
  try {
    const response = await savePerFieldData(req, apiName);

    if (!response.ok) {
      // Log detailed error info
      const errorBody = await response.text();
      Logger.error('Error saving data:', response.status, response.statusText, errorBody);
      throw new Error(`Failed to save data: ${response.status} ${response.statusText}`);
    }

    // 7. On success, update local Redux/form cache based on page
    const savingStateMap: Record<string, (n: string, v: any, d: any, bs: any) => void> = {
      'company-info-page': savingStateData,
      'ownership-info-page': savingStateData,
      'individual-customer-info-page': savingStateDataIndividual,
      'jt-basic-info-page': savingStateDataJT,
      'coowner-info-page': savingStateDataJT,
    };
    const savingStateFn = savingStateMap[pageName];
    if (savingStateFn) {
      savingStateFn(nodeName, value, dispatch, businessState);
    }

  } catch (error) {
    // 8. Log any unexpected errors during API call
    Logger.error('Error saving data:', error);
  }
};

/**
 * This method basically accepts a set of values that we need to 
 * pass to the onboardingForm API, and sets there value as ''
 * to handle the special usecases
 * @param keys 
 * @returns 
 */
function createRequestFromKeys(...keys: any[]) {
    const request: any = {};
    keys.forEach(key => {
      request[key] = '';
    });
    return request;
  }


  /**
   * Calling the respective autoSave API for the KYC pages
   * @param req 
   * @param apiName 
   * @returns 
   */
  export async function savePerFieldData(req: any, apiName: string) {
    try {
        const data: any = await httpClient.post(getCustomerUrl(apiName,false), req).then((response: any) => {
            return response
        })
            .catch((error: any) => {
                return { ...error };
            })
        return data;
    } catch (err) {
        return err;
    }
}

/**
 * This method saves the data in the state for the business details page
 * and the ownership details page
 * @param fieldName 
 * @param fieldValue 
 * @param dispatch 
 * @param businessState 
 */
export const savingStateData = (fieldName: string, fieldValue: any, dispatch: any, businessState: any) => {
    const businessStateCopy =  {...businessState?.data};
    const existingBusinessDetails = businessStateCopy['businessDetailsData'] || {};
    businessStateCopy['businessDetailsData'] = {
        ...existingBusinessDetails,
        [fieldName]: fieldValue
    };
    updateComponentData(dispatch, 'customer-kyc-data', { ...businessStateCopy })
}

/**
 * This method saves the data in the state for the individual details page
 * @param fieldName 
 * @param fieldValue 
 * @param dispatch 
 * @param individualState 
 */
const savingStateDataIndividual = (fieldName: string, fieldValue: any, dispatch: any, individualState: any) => {
    const individualStateCopy = {...individualState?.data}
    const existingIndividualDetails = individualStateCopy['individualData'] || {};
    individualStateCopy['individualData']= {
        ...existingIndividualDetails,
        [fieldName]: fieldValue
    };
    updateComponentData(dispatch, 'customer-kyc-individual-data', { ...individualStateCopy })
}

/**
 * This method saves the data in the state for the JT details page
 * @param fieldName 
 * @param fieldValue 
 * @param dispatch 
 * @param individualState 
 */
const savingStateDataJT = (fieldName: string, fieldValue: any, dispatch: any, jointOwnerState: any) => {
    const jointOwnerStateCopy = {...jointOwnerState?.data}
    const existingIJTOwnerDetails = jointOwnerStateCopy['primaryOwnerData'] || {};
    jointOwnerStateCopy['primaryOwnerData']= {
        ...existingIJTOwnerDetails,
        [fieldName]: fieldValue
    };
    updateComponentData(dispatch, 'customer-kyc-joint-owners-data', { ...jointOwnerStateCopy })
}

/**
 * This method converts the flat data object received from onboardingForm GET API 
 * to a JSON Object for easy interpretation
 * @param flatObj 
 * @returns 
 */
export function unflattenObject(flatObj: any) {
    const result = {};
  
    try{
        for (const flatKey in flatObj) {
            const value = flatObj[flatKey];
            const keys = flatKey
              .replace(/\[(\d+)\]/g, '.$1') // convert [0] to .0
              .split('.'); // split into path segments
        
            let current: any = result;
        
            keys.forEach((key, index) => {
              const isLast = index === keys.length - 1;
        
              // Determine if key should be array index
              const nextKey = keys[index + 1];
              const isNextArrayIndex = !isLast && /^\d+$/.test(nextKey);
        
              if (!isLast) {
                if (!(key in current)) {
                  current[key] = isNextArrayIndex ? [] : {};
                }
                current = current[key];
              } else {
                current[key] = value;
              }
            });
          }
    }catch(error){
        Logger.error("autoSaveFields.ts", "unflattenObject", "error", error);
        return {};
    }
  
    return result;
  }
  